/**
 * @file Typed detail-route helpers.
 */

import {
  getDetailRouteCollectionForItemType,
  getDetailRouteCollections,
  getDetailRouteItemTypeForCollection,
} from "./detailRouteMetadata.js";

export const DETAIL_ROUTE_COLLECTIONS = getDetailRouteCollections();

/**
 * Builds a canonical typed detail path.
 * @param {string} itemType Singular item type.
 * @param {string} itemId Document ID.
 * @returns {string|null} Typed detail path or null if invalid.
 */
export function buildDetailPath(itemType, itemId) {
  if (!itemType || !itemId) {return null;}

  const collectionName = getDetailRouteCollectionForItemType(itemType);
  if (!collectionName) {return null;}

  return `/${collectionName}/details/${itemId}`;
}

/**
 * Builds an app-internal attachment route.
 * @param {string} itemType Singular item type.
 * @param {string} itemId Document ID.
 * @param {string} attachmentId Logical attachment ID.
 * @param {string|null} [versionId] Optional version ID.
 * @returns {string|null} Attachment route or null if invalid.
 */
export function buildAttachmentDetailPath(
  itemType,
  itemId,
  attachmentId,
  versionId = null,
) {
  const detailPath = buildDetailPath(itemType, itemId);
  if (!detailPath || !attachmentId) {return null;}

  const params = new URLSearchParams();
  if (versionId) {
    params.set("version", versionId);
  }
  const query = params.toString();
  return `${detailPath}/attachments/${encodeURIComponent(attachmentId)}` +
    (query ? `?${query}` : "");
}

/**
 * Parses a canonical typed detail path.
 * @param {string} pathname URL pathname.
 * @returns {object|null} Parsed detail route data or null.
 */
export function parseDetailPath(pathname) {
  if (!pathname) {
    return null;
  }

  let routePath = pathname;
  let search = "";
  if (pathname.includes("?")) {
    const parsedUrl = new URL(pathname, "https://app.local");
    routePath = parsedUrl.pathname;
    search = parsedUrl.search;
  }

  const segments = routePath.split("/").filter(Boolean);
  const [
    collectionName,
    detailsSegment,
    itemId,
    attachmentSegment,
    attachmentId,
  ] = segments;
  if (!collectionName || detailsSegment !== "details" || !itemId ||
      (segments.length !== 3 && segments.length !== 5)) {
    return null;
  }
  if (segments.length === 5 &&
      (attachmentSegment !== "attachments" || !attachmentId)) {
    return null;
  }

  const itemType = getDetailRouteItemTypeForCollection(collectionName);
  if (!itemType) {
    return null;
  }

  return {
    collectionName,
    attachmentId: attachmentId ? decodeURIComponent(attachmentId) : null,
    itemId,
    itemType,
    pathname: `/${collectionName}/details/${itemId}`,
    versionId: search ?
      new URLSearchParams(search).get("version") || null :
      null,
  };
}

/**
 * Returns whether the pathname resembles a detail route but is not necessarily
 * a supported canonical route.
 * @param {string} pathname URL pathname.
 * @returns {boolean}
 */
export function isDetailLikePath(pathname) {
  if (!pathname) {return false;}

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) {return false;}

  return segments.includes("details") ||
    segments.includes("detail") ||
    segments[0] === "details";
}

/**
 * Parses a pathname into a route candidate for app-level resolution.
 * @param {string} pathname URL pathname.
 * @returns {{status: "valid", target: object}|{status: "invalid"}|null}
 */
export function parseDetailRouteCandidate(pathname) {
  const target = parseDetailPath(pathname);
  if (target) {
    return { status: "valid", target };
  }

  if (isDetailLikePath(pathname)) {
    return { status: "invalid" };
  }

  return null;
}

/**
 * Returns whether the pathname is a typed detail route.
 * @param {string} pathname URL pathname.
 * @returns {boolean}
 */
export function isDetailPath(pathname) {
  return Boolean(parseDetailPath(pathname));
}
